import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import JobCard from './JobCard';
import { FaBriefcase, FaMapMarkerAlt, FaArrowRight, FaUser } from 'react-icons/fa';

export default function JobSearchWidget({ limitResults = 3, onViewAllClick }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: '', location: '' });
  
  // Function to fetch poster details from organizations
  const fetchPosterDetails = async (posterId) => {
    if (!posterId) return { name: 'Company', photoURL: null };
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', posterId));
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        return {
          name: orgData.name || 'Company',
          photoURL: orgData.photoURL || orgData.logoURL || null,
          isOrganization: true
        };
      }
      return { name: 'Company', photoURL: null, isOrganization: true };
    } catch (error) {
      console.error('Error fetching organization details:', error);
      return { name: 'Company', photoURL: null, isOrganization: true };
    }
  };

  // Function to fetch organization details by ID
  const fetchOrganizationDetails = async (orgId) => {
    if (!orgId) return { name: null, photoURL: null };
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (!orgDoc.exists()) return { name: null, photoURL: null };
      const orgData = orgDoc.data();
      return {
        name: orgData.name || null,
        photoURL: orgData.photoURL || orgData.logoURL || null
      };
    } catch (error) {
      console.error('Error fetching organization:', error);
      return { name: null, photoURL: null };
    }
  };

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        let q = query(collection(db, 'jobPosts'), where('status', '==', 'open'));
        
        if (filters.role) {
          q = query(q, where('searchTerms', 'array-contains', filters.role.toLowerCase()));
        }
        if (filters.location) {
          // Search in both city and area fields of the location map
          const locationLower = filters.location.toLowerCase();
          q = query(q, where('searchTerms', 'array-contains', locationLower));
        }
        
        // Using postedAt to match the existing Firestore index
        q = query(q, orderBy('postedAt', 'desc'), limit(limitResults));
        
        const snapshot = await getDocs(q);
        
        // Process jobs and fetch organization details
        const jobsWithDetails = await Promise.all(snapshot.docs.map(async (doc) => {
          const jobData = { id: doc.id, ...doc.data() };
          
          // Always use organization details for posters
          if (jobData.posterId) {
            const posterDetails = await fetchPosterDetails(jobData.posterId);
            jobData.posterName = posterDetails.name;
            jobData.posterPhotoURL = posterDetails.photoURL;
            
            // For organization posters, set organization details as well
            if (jobData.posterType === 'organization') {
              jobData.organizationName = posterDetails.name;
              jobData.organizationPhotoURL = posterDetails.photoURL;
            }
          } else {
            // Fallback for jobs without poster info
            jobData.posterName = 'Company';
            jobData.posterPhotoURL = null;
          }
          
          return jobData;
        }));
        
        setJobs(jobsWithDetails);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        console.error('Error details:', {
          code: err.code,
          message: err.message,
          stack: err.stack
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobs();
  }, [filters, limitResults]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" id="jobs">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent tracking-tight">
          Find Your Next Opportunity
        </h2>
        <p className="mt-2 text-lg text-gray-500 font-medium">Discover jobs in the autism support community</p>
      </div>

      <div className="mb-8 bg-white rounded-xl shadow-lg p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <FaBriefcase className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Job title or keywords"
              className="pl-12 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              value={filters.role}
              onChange={(e) => setFilters({...filters, role: e.target.value})}
              aria-label="Search jobs by title or keywords"
            />
          </div>
          <div className="relative">
            <FaMapMarkerAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Location"
              className="pl-12 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              value={filters.location}
              onChange={(e) => setFilters({...filters, location: e.target.value})}
              aria-label="Search jobs by location"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-14 w-14 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="space-y-6 w-full max-w-4xl mx-auto">
          {jobs.map((job) => (
            <div key={job.id} className="w-full">
              <JobCard job={job} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-12 text-center">
        {onViewAllClick ? (
          <button
            onClick={onViewAllClick}
            className="inline-flex items-center px-8 py-3.5 text-base font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg"
          >
            View All Jobs <FaArrowRight className="ml-2.5" />
          </button>
        ) : (
          <Link
            to="/jobs"
            className="inline-flex items-center px-8 py-3.5 text-base font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg"
          >
            View All Jobs <FaArrowRight className="ml-2.5" />
          </Link>
        )}
      </div>
    </div>
  );
}
